
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Bell, ChevronDown, ChevronLeft, ClipboardList, Command, FileCheck2, FlaskConical,
  Gauge, Grid3x3, Hammer, Home, Inbox, LayoutDashboard, Menu, Package, PanelLeftClose,
  PanelLeftOpen, Search, Settings2, ShieldCheck, Wrench, X, BookOpen, Ship,
  Layers, BadgeCheck, UserRound,
} from "lucide-react";
import { Logo } from "@/components/logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { ROLES } from "@/lib/mock";
import { cn } from "@/lib/cn";
import { useOturum } from "@/lib/oturum";
import { cevir } from "@/lib/dil";

type NavItem = { ad: string; icon: React.ElementType; n?: number; on?: boolean; alt?: boolean; pasif?: boolean };
type NavGroup = { baslik: string; items: NavItem[] };

const NAV: NavGroup[] = [
  {
    baslik: cevir("General"),
    items: [
      { ad: cevir("Command Center"), icon: LayoutDashboard, on: true },
      { ad: cevir("My queue"), icon: Inbox, n: 6 },
    ],
  },
  {
    /* Prototipin üst navigasyonu birebir: sıra = süreç sırası, sayaçlar = prototip cnt() */
    baslik: cevir("Process"),
    items: [
      { ad: "Admin", icon: Settings2 },
      { ad: "MRB intake", icon: BookOpen, n: 1 },
      { ad: cevir("Receiving"), icon: Package, n: 2 },
      { ad: cevir("Disassembly"), icon: ClipboardList, n: 1 },
      { ad: cevir("Field list"), icon: Layers, n: 1 },
      { ad: cevir("Inspection verification"), icon: Search, n: 7 },
      { ad: cevir("Repair planning"), icon: Wrench, n: 4 },
      { ad: cevir("Component"), icon: FlaskConical, alt: true },
      { ad: "Fit-up", icon: Hammer, alt: true },
      { ad: "Weld map", icon: FileCheck2, alt: true },
      { ad: "Assembly", icon: BadgeCheck },
    ],
  },
  {
    baslik: cevir("Next bands (spec)"),
    items: [
      { ad: cevir("Station grid"), icon: Grid3x3, pasif: true },
      { ad: "Riser 360", icon: Ship, pasif: true },
      { ad: cevir("Hydrotest"), icon: Gauge, pasif: true },
      { ad: cevir("MRB package / DNV"), icon: ShieldCheck, pasif: true },
      { ad: cevir("Registers"), icon: Layers, pasif: true },
    ],
  },
];

export function ShellA({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [palette, setPalette] = useState(false);
  const [role, setRole] = useState(ROLES[2]!);
  const { ben, cikis } = useOturum();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((p) => !p);
      }
      if (e.key === "Escape") {
        setPalette(false);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="font-plex min-h-dvh bg-bg text-ink">
      {/* ---------- Yan menü (masaüstü) ---------- */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 hidden lg:flex flex-col border-r border-line bg-surface transition-[width] duration-200",
          collapsed ? "w-[64px]" : "w-[248px]",
        )}
      >
        <div className={cn("flex h-14 items-center border-b border-line", collapsed ? "justify-center" : "px-4")}>
          {collapsed ? (
            <span className="font-manrope text-lg font-extrabold tracking-tight">
              O<span className="text-orange">|</span>
            </span>
          ) : (
            <Logo height={22} />
          )}
        </div>
        <nav className="scroll-thin flex-1 overflow-y-auto py-2">
          {NAV.map((g) => (
            <div key={g.baslik} className="mb-2">
              {!collapsed && (
                <div className="px-4 pb-1 pt-3 text-2xs font-semibold uppercase tracking-[0.08em] text-ink-3">
                  {g.baslik}
                </div>
              )}
              {g.items.map((it) => (
                <a
                  key={it.ad}
                  href="#"
                  title={it.ad}
                  className={cn(
                    "group relative mx-2 my-0.5 flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-base text-ink-2 hover:bg-surface-2 hover:text-ink",
                    it.on && "bg-surface-2 font-semibold text-ink",
                    it.alt && !collapsed && "ml-6 py-[5px] text-sm",
                    it.pasif && "opacity-50",
                    collapsed && "justify-center px-0",
                  )}
                >
                  {it.on && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-orange" />}
                  <it.icon size={16} className={cn("shrink-0", it.on ? "text-orange" : "text-ink-3 group-hover:text-ink-2")} />
                  {!collapsed && <span className="flex-1 truncate">{it.ad}</span>}
                  {!collapsed && it.n ? (
                    <span className="rounded-full bg-surface-3 px-1.5 text-2xs font-semibold tnum text-ink-2">{it.n}</span>
                  ) : null}
                  {collapsed && it.n ? <span className="absolute right-2 top-1 h-1.5 w-1.5 rounded-full bg-orange" /> : null}
                </a>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-line p-2">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="flex w-full items-center justify-center gap-2 rounded-md px-2 py-2 text-xs text-ink-3 hover:bg-surface-2 hover:text-ink"
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <><PanelLeftClose size={16} /> {cevir("Collapse")}</>}
          </button>
        </div>
      </aside>

      {/* ---------- Mobil çekmece ---------- */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[84%] max-w-[300px] bg-surface shadow-[var(--shadow-lg)]">
            <div className="flex h-14 items-center justify-between border-b border-line px-4">
              <Logo height={22} />
              <button onClick={() => setMobileOpen(false)} className="rounded-md p-1.5 text-ink-2 hover:bg-surface-2"><X size={18} /></button>
            </div>
            <nav className="scroll-thin h-[calc(100dvh-56px)] overflow-y-auto py-2">
              {NAV.map((g) => (
                <div key={g.baslik} className="mb-2">
                  <div className="px-4 pb-1 pt-3 text-2xs font-semibold uppercase tracking-[0.08em] text-ink-3">{g.baslik}</div>
                  {g.items.map((it) => (
                    <a key={it.ad} href="#" className={cn("mx-2 my-0.5 flex items-center gap-3 rounded-md px-3 py-2.5 text-base text-ink-2", it.on && "bg-surface-2 font-semibold text-ink", it.alt && "ml-6 py-2 text-base", it.pasif && "opacity-50")}>
                      <it.icon size={18} className={it.on ? "text-orange" : "text-ink-3"} />
                      <span className="flex-1">{it.ad}</span>
                      {it.n ? <span className="rounded-full bg-surface-3 px-2 text-xs font-semibold tnum">{it.n}</span> : null}
                    </a>
                  ))}
                </div>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* ---------- Üst çubuk ---------- */}
      <div className={cn("transition-[padding] duration-200", collapsed ? "lg:pl-[64px]" : "lg:pl-[248px]")}>
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-surface/90 px-3 backdrop-blur sm:px-4">
          <button onClick={() => setMobileOpen(true)} className="rounded-md p-2 text-ink-2 hover:bg-surface-2 lg:hidden" aria-label={cevir("Menu")}>
            <Menu size={20} />
          </button>
          <Link to="/" className="lg:hidden"><Logo height={20} /></Link>

          <button
            onClick={() => setPalette(true)}
            className="ml-auto hidden h-9 w-[320px] items-center gap-2 rounded-md border border-line bg-bg px-3 text-base text-ink-3 hover:border-line-strong md:flex"
          >
            <Search size={15} />
            <span className="flex-1 text-left">{cevir("Search riser, SN, work order…")}</span>
            <kbd className="rounded border border-line bg-surface px-1.5 font-mono text-2xs text-ink-3">⌘K</kbd>
          </button>
          <button onClick={() => setPalette(true)} className="ml-auto rounded-md p-2 text-ink-2 hover:bg-surface-2 md:hidden" aria-label={cevir("Search")}><Search size={19} /></button>

          <span className="hidden items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-xs text-ink-2 sm:inline-flex">
            <span className="h-1.5 w-1.5 rounded-full bg-ok" /> {cevir("Cycle 2024–29 · B1–B4")}
          </span>
          <button className="relative rounded-md p-2 text-ink-2 hover:bg-surface-2" aria-label={cevir("Notifications")}>
            <Bell size={18} />
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-orange ring-2 ring-surface" />
          </button>
          <ThemeToggle />
          <div className="group relative">
            <button className="flex h-9 items-center gap-2 rounded-md border border-line bg-surface pl-1.5 pr-2 hover:border-line-strong">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-navy text-2xs font-bold text-white dark:text-[#0a141c]">
                {role.ad.split(" ").map((s) => s[0]).join("").slice(0, 2)}
              </span>
              <span className="hidden text-left sm:block">
                <span className="block text-sm font-semibold leading-tight">{role.ad.split(" ")[0]}</span>
                <span className="block text-2xs leading-tight text-ink-3">{role.rol}</span>
              </span>
              <ChevronDown size={14} className="text-ink-3" />
            </button>
            <div className="invisible absolute right-0 top-full z-40 mt-1 w-64 rounded-md border border-line bg-surface p-1 opacity-0 shadow-[var(--shadow-lg)] transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
              {ben && (
                <div className="border-b border-line px-2 py-2">
                  <div className="text-sm font-semibold">{ben.ad}</div>
                  <div className="text-xs text-ink-3">{ben.eposta} · {cevir(ben.rolEtiket)}</div>
                  <button onClick={() => void cikis()} className="mt-2 w-full rounded border border-line px-2 py-1 text-sm text-ink-2 hover:bg-surface-2">{cevir("Log out")}</button>
                </div>
              )}
              <div className="px-2 py-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-3">{cevir("Role simulation (mock)")}</div>
              {ROLES.map((r) => (
                <button key={r.id} onClick={() => setRole(r)} className={cn("flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-base hover:bg-surface-2", r.id === role.id && "bg-surface-2 font-semibold")}>
                  <UserRound size={14} className="text-ink-3" />
                  <span className="flex-1">{r.ad}</span>
                  <span className="text-xs text-ink-3">{r.kisa}</span>
                </button>
              ))}
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1480px] px-3 pb-24 pt-4 sm:px-5 lg:pb-8">{children}</main>
      </div>

      {/* ---------- Mobil alt sekme ---------- */}
      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-line bg-surface/95 backdrop-blur lg:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {[
          { ad: cevir("Command Center"), icon: Home, on: true },
          { ad: cevir("My queue"), icon: Inbox, n: 6 },
          { ad: cevir("Search"), icon: Command },
          { ad: cevir("Grid"), icon: Grid3x3 },
          { ad: cevir("Menu"), icon: Menu },
        ].map((t) => (
          <button
            key={t.ad}
            onClick={() => (t.ad === cevir("Menu") ? setMobileOpen(true) : t.ad === cevir("Search") ? setPalette(true) : null)}
            className={cn("relative flex flex-col items-center gap-0.5 py-2 text-2xs", t.on ? "text-orange font-semibold" : "text-ink-3")}
          >
            <t.icon size={20} />
            {t.ad}
            {t.n ? <span className="absolute right-[22%] top-1 rounded-full bg-orange px-1 text-2xs font-bold text-white">{t.n}</span> : null}
          </button>
        ))}
      </nav>

      {/* ---------- ⌘K komut paleti ---------- */}
      {palette && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 pt-[12vh]" onClick={() => setPalette(false)}>
          <div className="w-full max-w-[600px] overflow-hidden rounded-lg border border-line bg-surface shadow-[var(--shadow-lg)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-line px-3">
              <Search size={16} className="text-ink-3" />
              <input autoFocus placeholder="R061, 1307323-1, W-25069-0041, “fit-up”…" className="h-12 flex-1 bg-transparent text-base outline-none placeholder:text-ink-3" />
              <kbd className="rounded border border-line px-1.5 font-mono text-2xs text-ink-3">esc</kbd>
            </div>
            <div className="max-h-[50vh] overflow-y-auto p-2 text-base">
              <div className="px-2 py-1 text-2xs font-semibold uppercase tracking-wide text-ink-3">{cevir("Risers")}</div>
              {[cevir("R061 · 1307323-1 · S6 Repair · MB waiting for spare part"), cevir("R117 · 1281597-1 · S5 · waiting for Disposition (19 d)"), cevir("R157 · ex-Transocean · S2 · ID verification")].map((s) => (
                <button key={s} className="flex w-full items-center gap-2 rounded px-2 py-2 text-left hover:bg-surface-2"><Ship size={14} className="text-ink-3" />{s}</button>
              ))}
              <div className="px-2 py-1 text-2xs font-semibold uppercase tracking-wide text-ink-3">{cevir("Quick action")}</div>
              {[cevir("Start new Disassembly"), cevir("Upload SURVREP"), cevir("Upload Disposition report"), cevir("Generate weekly report (E-03)")].map((s) => (
                <button key={s} className="flex w-full items-center gap-2 rounded px-2 py-2 text-left hover:bg-surface-2"><ChevronLeft size={14} className="rotate-180 text-ink-3" />{s}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
